// Companion/blocks/StudySetVideoStage.tsx
//
// The video half of a study set: one stage plus an "In this set" rail
// (ORA-686).
//
// It replaces the "Video 2 / Video 3" chips, which appended a second and a
// third player *below* the first — three stacked players in a thread, and no
// way to see what the set held without opening all of them. A set of three
// videos is a playlist, so it is drawn as one: the video that is playing, and
// three thumbnails that say how long each is and how far the student got.
//
// Only the active video is mounted. A hidden `VideoPlayer` is not idle — it
// fetches its versions, claims the chat anchor and would report progress for a
// video nobody is watching — so switching tiles remounts the stage rather than
// revealing a player that was there all along.

import { useMemo, useState, type ReactNode, type Ref } from 'react';
import { Check, Play } from '@phosphor-icons/react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { VideoPlaybackProgress } from '@/services/videoPlayback';
import type { ChatStudySetVideo } from '@/types/chatBlocks';
import type { ChatAnchorInput, ChatSurface } from '@/types/chatEnvelope';
import type { VideoPlayerControls } from '@/components/ora/Videos/VideoPlayer';
import { VideoBlockBody } from './bodies/VideoBlockBody/VideoBlockBody';

/** A set item the stage draws, narrowed to the video kind by the caller. */
export interface StudySetVideoItem {
  ord: number;
  done: boolean;
  video: ChatStudySetVideo;
}

export interface StudySetVideoStageProps {
  setId: string;
  items: StudySetVideoItem[];
  surface: ChatSurface;
  /** The ords the block counts as done, including ones finished in-thread. */
  doneOrds: Set<number>;
  /**
   * Which video is on the stage. Controlled by the block, which prints the
   * position in the set's header.
   */
  activeOrd: number;
  onActiveOrdChange: (ord: number) => void;
  /** The student finished this video here; the footer's count moves with it. */
  onWatched: (ord: number) => void;
  /** Claim the chat anchor for the video on the stage. */
  claim: (anchor: ChatAnchorInput) => void;
  /**
   * The course video step's hooks into the inline player (ORA-1055): its
   * imperative controls, its live-question observer, and what to draw right
   * under the player (Raise Your Hand). The chat's own sets pass none.
   */
  playerControlRef?: Ref<VideoPlayerControls>;
  onLiveQuestionOpenChange?: (isOpen: boolean) => void;
  belowPlayer?: ReactNode;
  /** Where the player's live question panel goes (a course step: beside). */
  liveQuestionPanelPlacement?: 'auto' | 'beside';
  /** A live question was answered on the active video. */
  onLiveQuestionAnswered?: () => void;
}

/**
 * `m:ss`, the way every other duration in the video UI reads. An hour-long
 * lecture prints as `64:20` rather than `1:04:20`; the library has none, and a
 * third field on a thumbnail chip costs more than it says.
 */
export const formatVideoDuration = (seconds: number): string => {
  const total = Math.max(0, Math.round(seconds));

  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

const StudySetVideoStage = ({
  setId,
  items,
  surface,
  doneOrds,
  activeOrd,
  onActiveOrdChange,
  onWatched,
  claim,
  playerControlRef,
  onLiveQuestionOpenChange,
  belowPlayer,
  liveQuestionPanelPlacement,
  onLiveQuestionAnswered,
}: StudySetVideoStageProps) => {
  const isWatched = (item: StudySetVideoItem) =>
    item.done || doneOrds.has(item.ord);

  /**
   * The rail's progress, seeded from the wire and moved by the player.
   *
   * The server only re-sends a set on the next serve, so without this a
   * student who watches video 1 here sees its tile sit at zero until the
   * thread is rebuilt. Keyed by video id rather than ord because that is what
   * the player reports against.
   */
  const [watchedPercent, setWatchedPercent] = useState<Map<string, number>>(
    () => new Map()
  );

  const active = useMemo(
    () => items.find((item) => item.ord === activeOrd) ?? items[0],
    [activeOrd, items]
  );

  if (!active) return null;

  const watchedCount = items.filter(isWatched).length;
  // A set is capped at three videos, so three tiles fill the width from the
  // first breakpoint up and only a longer set ever has to scroll.
  const tilesFit = items.length <= 3;

  const percentFor = (item: StudySetVideoItem) =>
    isWatched(item)
      ? 100
      : (watchedPercent.get(item.video.videoId) ?? item.video.progressPercent);

  const handlePlaybackProgress = (
    item: StudySetVideoItem,
    progress: VideoPlaybackProgress
  ) => {
    setWatchedPercent((current) =>
      new Map(current).set(item.video.videoId, progress.progressPercent)
    );

    // Completion is the server's word — the player writes it at the 90%
    // threshold and the set's item is marked done by the same request — but
    // the block only hears about it if the player says so, so this is what
    // moves the footer's "N of M done" before the next serve.
    if (progress.isCompleted) onWatched(item.ord);
  };

  return (
    <div className="flex flex-col gap-3" data-testid="study-set-video-stage">
      {/* Keyed by video id: switching tiles must tear the player down rather
          than hand it a different video, which would leave the old timeline's
          progress writes in flight. */}
      <div key={active.video.videoId} data-testid="study-set-video-active">
        <VideoBlockBody
          item={{
            id: active.video.videoId,
            title: active.video.title ?? '',
            description: active.video.description ?? '',
            thumbnailUrl: active.video.thumbnailUrl ?? '',
          }}
          videoDisplay={surface === 'chat_page' ? 'inline' : 'card'}
          startSecond={active.video.startSecond ?? undefined}
          studySetId={setId}
          liveQuestionPanelPlacement={liveQuestionPanelPlacement}
          onLiveQuestionAnswered={onLiveQuestionAnswered}
          onPlaybackProgress={(progress) =>
            handlePlaybackProgress(active, progress)
          }
          onAnchorChange={(anchor) => {
            claim({
              type: 'video',
              videoId:
                typeof anchor?.id === 'string' && anchor.id
                  ? anchor.id
                  : active.video.videoId,
              timestampSec: active.video.startSecond ?? null,
              playing: true,
            } satisfies ChatAnchorInput);
          }}
          playerControlRef={playerControlRef}
          onLiveQuestionOpenChange={onLiveQuestionOpenChange}
        />
      </div>

      {belowPlayer}

      {/* The title and the summary are deliberately not repeated here: the
          inline player prints both in its own info panel, and the card stage
          puts the title on the thumbnail and the description under it. */}

      {items.length > 1 ? (
        <div className="flex flex-col gap-2">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-xxs font-medium tracking-wide text-fg-secondary uppercase">
              In this set
            </span>
            <span
              className="text-xs text-fg-secondary"
              data-testid="study-set-video-watched"
            >
              {watchedCount} of {items.length} watched
            </span>
          </div>
          {/* The active tile's ring is a box-shadow outside its box, and the
              scroll container clips it; 2px of padding (pulled back by the
              matching negative margin) gives the ring room on every side. */}
          <div
            className="no-scrollbar -m-0.5 flex snap-x gap-2 overflow-x-auto p-0.5"
            role="group"
            aria-label="Videos in this set"
          >
            {items.map((item, index) => {
              const watched = isWatched(item);
              const isActive = item.ord === active.ord;
              const percent = percentFor(item);
              const title = item.video.title || 'Untitled video';

              return (
                <button
                  key={item.ord}
                  type="button"
                  data-testid="study-set-video-tile"
                  data-item-ord={item.ord}
                  aria-current={isActive || undefined}
                  aria-label={`Video ${index + 1}: ${title}${
                    watched ? ', watched' : ''
                  }`}
                  onClick={() => onActiveOrdChange(item.ord)}
                  className={cn(
                    'flex min-h-11 w-40 shrink-0 snap-start flex-col overflow-hidden rounded-lg border border-border-subtle bg-surface-muted text-left transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    tilesFit && 'mobile:w-auto mobile:min-w-0 mobile:flex-1',
                    // The mockup's outline is the same teal as the number
                    // badge, which is `status-success` in this palette;
                    // `fg-accent` is purple in light mode.
                    isActive && 'ring-2 ring-status-success',
                    watched && 'opacity-60'
                  )}
                >
                  <span className="relative block aspect-video w-full bg-surface-muted">
                    {item.video.thumbnailUrl ? (
                      <img
                        src={item.video.thumbnailUrl}
                        alt=""
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <span className="flex h-full w-full items-center justify-center text-fg-secondary">
                        <Play weight="fill" aria-hidden="true" />
                      </span>
                    )}
                    <Badge
                      className="absolute top-1.5 left-1.5"
                      variant={isActive || watched ? 'success' : 'secondary'}
                    >
                      {watched ? (
                        <Check
                          weight="bold"
                          aria-hidden="true"
                          data-testid="study-set-video-tile-check"
                        />
                      ) : (
                        index + 1
                      )}
                    </Badge>
                    {item.video.durationSeconds !== null ? (
                      <span className="absolute right-1.5 bottom-1.5 rounded-xs bg-surface-raised px-1.5 py-0.5 text-xxs font-medium text-fg-primary">
                        {formatVideoDuration(item.video.durationSeconds)}
                      </span>
                    ) : null}
                    {percent > 0 && percent < 100 ? (
                      <span className="absolute inset-x-0 bottom-0 block h-1 bg-surface-raised">
                        <span
                          className="block h-full bg-status-success"
                          data-testid="study-set-video-tile-progress"
                          style={{ width: `${percent}%` }}
                        />
                      </span>
                    ) : null}
                  </span>
                  {/* The clamp and the padding live on different elements: a
                      clamped box clips at its padding edge, so a third line
                      showed through the bottom padding at 390px. */}
                  <span className="block p-2">
                    <span
                      className={cn(
                        'line-clamp-2 text-xs',
                        watched ? 'text-fg-secondary' : 'text-fg-primary'
                      )}
                    >
                      {title}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default StudySetVideoStage;
